import type { ReactNode } from "react";
import { SiReddit, SiX } from "@icons-pack/react-simple-icons";
import { motion, useReducedMotion } from "motion/react";

import LinkedInIcon from "@/assets/images/vectors/platforms/linkedin.svg?react";
import WabDockIcon from "@/landing/wab/assets/wab-dock-icon.png?url";

// Ported from the /wab2 "Why Wonda" antidetect schema (SchemaStage). Coding
// agents on the left wire into a central WAB hub, which drives the platforms on
// the right, with green pulses flowing along the edges. Rendered in the
// onboarding guide's right panel in place of the old install walkthrough video.
// The "linkedin" variant mirrors the wondatwin.com landing: LinkedIn is the
// only platform on the right.

type GraphNode = {
  label: string;
  // percentage anchors that line up with the SVG viewBox endpoints below
  leftPct: string;
  topPct: string;
  content: ReactNode;
};

type Wire = { d: string; delay: number };

const AGENT_NODES: GraphNode[] = [
  {
    label: "Claude Cowork",
    leftPct: "13%",
    topPct: "14%",
    content: (
      <img
        src="/icons/claude.svg"
        alt=""
        className="size-5 opacity-80 [filter:brightness(0)]"
      />
    ),
  },
  {
    label: "Claude Code",
    leftPct: "13%",
    topPct: "38%",
    content: (
      <img
        src="/icons/claude-code-bot.png"
        alt=""
        className="size-5 opacity-80 [filter:brightness(0)]"
      />
    ),
  },
  {
    label: "Codex",
    leftPct: "13%",
    topPct: "62%",
    content: (
      <img
        src="/icons/codex.svg"
        alt=""
        className="size-5 opacity-80 [filter:brightness(0)]"
      />
    ),
  },
  {
    label: "OpenClaw",
    leftPct: "13%",
    topPct: "86%",
    content: (
      <img
        src="/icons/openclaw.svg"
        alt=""
        className="size-5 opacity-80 [filter:brightness(0)]"
      />
    ),
  },
];

const PLATFORM_NODES_DEFAULT: GraphNode[] = [
  {
    label: "LinkedIn",
    leftPct: "87%",
    topPct: "18%",
    content: (
      <LinkedInIcon className="size-5" style={{ color: "var(--ink)" }} />
    ),
  },
  {
    label: "X",
    leftPct: "87%",
    topPct: "50%",
    content: <SiX color="currentColor" className="size-5 text-[var(--ink)]" />,
  },
  {
    label: "Reddit",
    leftPct: "87%",
    topPct: "82%",
    content: (
      <SiReddit color="currentColor" className="size-5 text-[var(--ink)]" />
    ),
  },
];

const PLATFORM_NODES_LINKEDIN: GraphNode[] = [
  {
    label: "LinkedIn",
    leftPct: "87%",
    topPct: "50%",
    content: (
      <LinkedInIcon className="size-6" style={{ color: "var(--ink)" }} />
    ),
  },
];

// viewBox is 0..300 x, 0..200 y. preserveAspectRatio="none" stretches it to
// fill, so these endpoints track the percentage-anchored HTML nodes above.
const AGENT_WIRES: Wire[] = [
  { d: "M42 28 C 96 28, 106 100, 150 100", delay: 0 },
  { d: "M42 76 C 96 76, 116 100, 150 100", delay: 0.25 },
  { d: "M42 124 C 96 124, 116 100, 150 100", delay: 0.5 },
  { d: "M42 172 C 96 172, 106 100, 150 100", delay: 0.75 },
];

const PLATFORM_WIRES_DEFAULT: Wire[] = [
  { d: "M150 100 C 190 100, 204 36, 258 36", delay: 0.15 },
  { d: "M150 100 C 204 100, 204 100, 258 100", delay: 0.4 },
  { d: "M150 100 C 190 100, 204 164, 258 164", delay: 0.65 },
];

const PLATFORM_WIRES_LINKEDIN: Wire[] = [
  { d: "M150 100 C 204 100, 204 100, 258 100", delay: 0.4 },
];

function GraphNodeTile({ node }: { node: GraphNode }) {
  return (
    <div
      className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1.5"
      style={{ left: node.leftPct, top: node.topPct }}
    >
      <div
        className="flex size-11 items-center justify-center border"
        style={{
          borderColor: "var(--hair-strong)",
          background: "var(--surface)",
        }}
      >
        {node.content}
      </div>
      <span
        className="font-mono text-[10px] tracking-tight whitespace-nowrap"
        style={{ color: "var(--cli-text-muted)" }}
      >
        {node.label}
      </span>
    </div>
  );
}

export function AntidetectGraph({
  variant = "default",
}: {
  variant?: "default" | "linkedin";
}) {
  const animate = !useReducedMotion();
  const platformNodes =
    variant === "linkedin" ? PLATFORM_NODES_LINKEDIN : PLATFORM_NODES_DEFAULT;
  const wires = [
    ...AGENT_WIRES,
    ...(variant === "linkedin"
      ? PLATFORM_WIRES_LINKEDIN
      : PLATFORM_WIRES_DEFAULT),
  ];

  return (
    <div className="flex h-full flex-col justify-center px-8">
      <div className="relative h-[300px] w-full sm:h-[320px]">
        {/* Wires + flowing pulses */}
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 300 200"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            {/* Edges grey, center green: the diagram converges on the WAB hub. */}
            <linearGradient
              id="antidetect-wire"
              gradientUnits="userSpaceOnUse"
              x1="42"
              y1="0"
              x2="258"
              y2="0"
            >
              <stop offset="0" stopColor="var(--hair-strong)" />
              <stop offset="0.5" stopColor="var(--green)" />
              <stop offset="1" stopColor="var(--hair-strong)" />
            </linearGradient>
          </defs>
          {wires.map((wire) => (
            <path
              key={`base-${wire.d}`}
              d={wire.d}
              stroke="url(#antidetect-wire)"
              strokeWidth={1.5}
              fill="none"
              strokeLinecap="round"
              opacity={0.7}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {animate &&
            wires.map((wire) => (
              <motion.path
                key={`pulse-${wire.d}`}
                d={wire.d}
                stroke="var(--green)"
                strokeWidth={2}
                fill="none"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                pathLength={1}
                strokeDasharray="0.12 0.88"
                initial={{ strokeDashoffset: 1 }}
                animate={{ strokeDashoffset: [1, 0] }}
                transition={{
                  duration: 1.9,
                  ease: "linear",
                  repeat: Infinity,
                  delay: wire.delay,
                }}
              />
            ))}
        </svg>

        {/* Agent + platform nodes */}
        {AGENT_NODES.map((node) => (
          <GraphNodeTile key={node.label} node={node} />
        ))}
        {platformNodes.map((node) => (
          <GraphNodeTile key={node.label} node={node} />
        ))}

        {/* Central WAB hub with green halo */}
        <div
          className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
          style={{ left: "50%", top: "50%" }}
        >
          <div className="relative flex flex-col items-center">
            {animate && (
              <motion.div
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-1/2 size-28 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, color-mix(in srgb, var(--green) 55%, transparent), transparent 68%)",
                }}
                animate={{
                  opacity: [0.55, 0.95, 0.55],
                  scale: [0.92, 1.04, 0.92],
                }}
                transition={{
                  duration: 2.6,
                  ease: "easeInOut",
                  repeat: Infinity,
                }}
              />
            )}
            <img
              src={WabDockIcon}
              alt="Wonda Automation Browser"
              className="relative size-16 rounded-2xl"
            />
          </div>
        </div>
      </div>

      {/* Tagline under the whole diagram, mirrored from the wondatwin landing */}
      <p
        className="mt-6 px-6 text-center text-sm leading-snug font-medium"
        style={{ color: "var(--cli-text-secondary)" }}
      >
        {variant === "linkedin"
          ? "#1 Stealth browser for LinkedIn to keep your accounts safe & secure"
          : "#1 Stealth browser to keep your accounts safe & secure"}
      </p>
    </div>
  );
}
